/**
 * useRequireAuth — single gate for "this feature needs an account" flows.
 *
 * Usage:
 *   const { gate, RequireAuthModal } = useRequireAuth();
 *   <button onClick={gate(() => doProtectedThing(), { feature: "basket" })}>
 *
 *   {RequireAuthModal}   // mount once in the same component
 *
 * `gate(fn, opts)` returns a new handler:
 *   • If the user IS authenticated, it simply calls `fn(...)` with the
 *     same arguments — fully transparent.
 *   • If NOT authenticated, it shows a sign-up modal (in the user's UI
 *     language) explaining why this feature needs an account, with copy
 *     contextualised by `opts.feature` (basket / lesson / ai / notebook /
 *     beit_midrash / bookmark / highlight / note / who_says_what). The
 *     pending callback is dropped; the modal does not auto-execute
 *     after sign-up.
 *
 * The modal preserves current page context — closing it does not
 * refresh or navigate. Clicking the sign-up CTA navigates to
 * /auth?next={current_path} so the user lands back where they started.
 */
import { useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { GraduationCap } from "lucide-react";

// Whitelist of feature keys → matches i18n key suffix. Anything not in
// this list falls back to "default", same as before.
const FEATURE_KEYS = new Set([
  "default", "lesson", "basket", "ai", "who_says_what",
  "notebook", "beit_midrash", "bookmark", "highlight", "note",
]);

export default function useRequireAuth() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("default");

  const gate = useCallback(
    (fn, opts = {}) => {
      const featureKey = opts.feature || "default";
      return (...args) => {
        if (user) {
          return fn(...args);
        }
        setFeature(featureKey);
        setOpen(true);
        return undefined;
      };
    },
    [user]
  );

  const goToAuth = () => {
    setOpen(false);
    // Use a relative `next` path so we land back on the same page after
    // sign-up — preserves verse/chapter/scroll context as best the SPA
    // router can.
    const next = location.pathname + location.search;
    navigate(`/auth?next=${encodeURIComponent(next)}`);
  };

  const featureKey = FEATURE_KEYS.has(feature) ? feature : "default";
  const title = t(`require_auth.${featureKey}_title`);
  const body = t(`require_auth.${featureKey}_body`);

  const RequireAuthModal = useMemo(
    () => (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          dir={isEn ? "ltr" : "rtl"}
          data-testid="require-auth-dialog"
          className="max-w-md border-gold/40"
        >
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mb-2">
              <GraduationCap className="h-7 w-7 text-gold" strokeWidth={1.5} />
            </div>
            <DialogTitle className="font-heading text-center text-2xl text-foreground">
              {title}
            </DialogTitle>
            <DialogDescription className="text-center leading-relaxed pt-2 text-foreground/80">
              {body}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              data-testid="require-auth-signup-btn"
              onClick={goToAuth}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading min-h-[44px]"
            >
              {t("require_auth.signup_now")}
            </Button>
            <Button
              data-testid="require-auth-dismiss-btn"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground min-h-[40px]"
            >
              {t("require_auth.maybe_later")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, title, body, isEn]
  );

  return { gate, isAuthed: !!user, RequireAuthModal };
}
